/**
 * Volume per gruppo muscolare — vista singola scheda (coach-only).
 */
import {
  getEffectiveGroups, buildLibraryMap, tallyWorkout,
  volumeLevel, LEVEL_STYLES, formatVolume,
} from "./muscleVolume";

export default function MuscleVolumeSummary({ exercises, exerciseLibrary, userMappings }) {
  if (!exercises || exercises.length === 0) return null;

  const libMap = buildLibraryMap(exerciseLibrary, userMappings);
  const { tally, matched } = tallyWorkout(exercises, libMap, {
    countSecondary: userMappings?.count_secondary !== false,
    secondaryWeight: typeof userMappings?.secondary_weight === "number"
      ? userMappings.secondary_weight : 0.5,
  });
  const groups = getEffectiveGroups(userMappings);

  const rows = groups
    .map((g) => ({ ...g, total: tally[g.key] || 0 }))
    .filter((g) => g.total > 0);

  if (rows.length === 0) return null;

  const unmatched = (exercises.length || 0) - matched;

  return (
    <div
      className="mx-4 mb-3 mt-1 rounded-lg border border-[#1E1E1E] bg-[#0A0A0A]/60 p-3"
      data-testid="muscle-volume-summary"
      aria-label="Volume per gruppo muscolare (solo coach)"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            Volume per gruppo muscolare
          </span>
          <span
            className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4FF00]/10 text-[#D4FF00]"
            title="Visibile solo al coach"
          >
            Coach
          </span>
        </div>
        {unmatched > 0 && (
          <span className="text-[9px] text-zinc-600" title="Esercizi non mappati su gruppo muscolare">
            {unmatched} non mappati
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((g) => {
          const level = volumeLevel(g.total);
          const s = LEVEL_STYLES[level] || LEVEL_STYLES.basso;
          return (
            <div
              key={g.key}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full ring-1 ${s.bg} ${s.ring}`}
              data-testid={`muscle-volume-${g.key}`}
              data-level={level}
              title={`${g.label}: ${formatVolume(g.total)} serie (${level})`}
            >
              <span className={`text-[10px] font-semibold ${s.text}`}>{g.label}</span>
              <span className={`text-[10px] font-bold tabular-nums ${s.text}`}>{formatVolume(g.total)}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#1E1E1E]">
        <LegendDot level="basso" label="Basso (<5)" />
        <LegendDot level="medio" label="Medio (5-9)" />
        <LegendDot level="alto" label="Alto (≥10)" />
      </div>
    </div>
  );
}

function LegendDot({ level, label }) {
  const dotColor =
    level === "alto" ? "bg-[#D4FF00]"
      : level === "medio" ? "bg-[#007AFF]"
        : "bg-zinc-400";
  return (
    <div className="flex items-center gap-1" data-testid={`legend-${level}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      <span className="text-[9px] text-zinc-600">{label}</span>
    </div>
  );
}
